import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { TypoCallout, TypoCaption, TypoFootnote } from '@/components/Typography.tsx';
import LADDER_SOLO_SETS from '@/graphql/queries/ladder-solo-sets.graphql';
import LADDER_SOLO_MATCH from '@/graphql/queries/ladder-solo-match.graphql';

import { SoloSet, SoloSets, TONE_TEXT, formatDate, formatSeconds, getSoloResult } from './common.ts';
import { Badge, EmptyState, InlineLoader, Modal, Panel, ReviewLink, RoundBars, Table } from './components.tsx';

interface SoloMatchQuestion {
  examQuestionId: number;
  position: number;
  correct: boolean | null;
  won: boolean | null;
  playerTimeSeconds: number | null;
  beatTimeSeconds: number;
}

const LadderSolo = () => {
  const { data, loading } = useQuery(LADDER_SOLO_SETS);
  const [selected, setSelected] = useState<SoloSet | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  if (loading && !data) return <InlineLoader />;

  const solo: SoloSets | undefined = data?.ladderSoloSets;
  const sets = [...(solo?.sets ?? [])]
    .filter(set => set.played || set.answers.length)
    .sort((a, b) => b.position - a.position);

  return (
    <Panel
      title="Solo matches"
      action={solo && <Badge>{solo.played} played</Badge>}
    >
      <TypoCallout className="block mb-4 text-neutral2-text-soft">
        Five rounds against the clock: win a round by answering correctly within the time to beat.
      </TypoCallout>
      {!sets.length ? <EmptyState text="No solo matches played yet." /> : (
        <Table
          head={[
            { label: 'Match' },
            { label: 'Score', className: 'text-center' },
            { label: 'Rounds' },
            { label: 'Date' },
            { label: '', className: 'text-right' },
          ]}
        >
          {sets.map(set => {
            const result = getSoloResult(set);
            const roundsWon = set.roundsWon.filter(Boolean).length;

            return (
              <tr key={set.position} className="hover:bg-shade-5">
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <TypoCallout className="font-semibold text-neutral2-text-strong">
                      Solo Match #{set.position}
                    </TypoCallout>
                    <Badge tone={result.tone}>{result.label}</Badge>
                  </div>
                </td>
                <td className={`px-3 py-3 text-center font-semibold whitespace-nowrap ${TONE_TEXT[result.tone]}`}>
                  {roundsWon} – {set.roundsWon.length - roundsWon}
                </td>
                <td className="px-3 py-3"><RoundBars results={set.roundsWon} total={5} /></td>
                <td className="px-3 py-3 whitespace-nowrap text-neutral2-text-softer">
                  {formatDate(set.clientExam?.updated)}
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-end gap-3">
                    <button
                      onClick={() => { setSelected(set); setDetailsOpen(true); }}
                      className="text-callout font-semibold text-primary hover:underline whitespace-nowrap"
                    >
                      Details →
                    </button>
                    {set.played && set.clientExam && (
                      <ReviewLink to={`/review/${set.exam.examId}/${set.clientExam.clientExamId}`} />
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </Table>
      )}
      {selected && <SoloDetails set={selected} open={detailsOpen} onClose={() => setDetailsOpen(false)} />}
    </Panel>
  );
};

const SoloDetails = ({ set, open, onClose }: { set: SoloSet; open: boolean; onClose: () => void }) => {
  const { data, loading } = useQuery(LADDER_SOLO_MATCH, { variables: { examId: set.exam.examId } });
  const questions: SoloMatchQuestion[] = [...(data?.ladderSoloMatch ?? [])].sort((a, b) => a.position - b.position);
  const result = getSoloResult(set);
  const roundsWon = set.roundsWon.filter(Boolean).length;

  return (
    <Modal open={open} title={`Solo Match #${set.position}`} onClose={onClose}>
      {loading ? <InlineLoader /> : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            {questions.map(question => (
              <div
                key={question.examQuestionId}
                className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm bg-neutral2"
              >
                <TypoCaption className="uppercase font-semibold text-neutral2-text-softer">
                  Round {question.position}
                </TypoCaption>
                {question.playerTimeSeconds === null ? (
                  <TypoCaption className="text-neutral2-text-weak">Not answered yet</TypoCaption>
                ) : (
                  <TypoFootnote className={`font-semibold ${TONE_TEXT[question.won ? 'success' : 'danger']}`}>
                    {question.correct ? '✓' : '✗'} {formatSeconds(question.playerTimeSeconds)}
                    <span className="font-normal text-neutral2-text-softer">
                      {' '}(beat {formatSeconds(question.beatTimeSeconds)})
                    </span>
                  </TypoFootnote>
                )}
              </div>
            ))}
          </div>
          <div className="py-3 rounded bg-neutral2 text-center">
            <TypoCaption className="uppercase font-semibold text-neutral2-text-softer">Final score</TypoCaption>
            <strong className="block text-title text-neutral2-text-strong">
              {roundsWon} – {set.roundsWon.length - roundsWon}
            </strong>
            <Badge tone={result.tone}>{result.label}</Badge>
          </div>
          <TypoCaption className="text-neutral-text-softer">
            A round counts as won when the answer is correct and inside the time to beat (adjusted for your
            accommodations).
          </TypoCaption>
        </div>
      )}
    </Modal>
  );
};

export default LadderSolo;
